/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ProductModel } from '@/api/types/products-types';
import { useLocale } from '@/hooks/useLocale';
import Head from 'next/head';
import { useMemo } from 'react';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { getBaseSchemaUrl } from '@/utils/common';
import { useAuthenticDiscoveries } from '@/features/customReviews/api/getAuthenticDiscoveries';
import { addAtPrefix } from '@/features/customReviews/utils/addAtPrefix';

type Props = {
  product: ProductModel;
};

export const AuthenticDiscoveriesMetaData = ({ product }: Props) => {
  const locale = useLocale();
  const router = useLegacyRouter();
  const baseUrl = getBaseSchemaUrl(locale);
  const pathname = getPathnameFromAsPath(router.asPath);

  const { data } = useAuthenticDiscoveries({
    skuId: product.itemId,
    canonical: `${baseUrl}${pathname}`,
  });

  const reviewsScript = useMemo(() => {
    if (!data?.reviews) {
      return '';
    }
    const { context, review, id } = data.reviews;
    return JSON.stringify({
      '@context': context,
      review: review.map(addAtPrefix),
      '@id': id,
    }).replace(/</g, '\\u003c');
  }, [data]);

  const ratingsScript = useMemo(() => {
    if (!data?.ratings) {
      return '';
    }
    const { context, aggregateRating, id } = data.ratings;
    return JSON.stringify({
      '@context': context,
      aggregateRating: addAtPrefix(aggregateRating),
      '@id': id,
    }).replace(/</g, '\\u003c');
  }, [data]);

  const reviewSummaryScript = useMemo(() => {
    if (!data?.reviewSummary) {
      return '';
    }
    const { context, review, id } = data.reviewSummary;
    return JSON.stringify({
      '@context': context,
      review: addAtPrefix(review),
      '@id': id,
    }).replace(/</g, '\\u003c');
  }, [data]);

  return (
    <Head>
      {reviewsScript && (
        <script
          key="bv-geo-jsonld-reviews-data"
          id="bv-geo-jsonld-reviews-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewsScript }}
        />
      )}
      {ratingsScript && (
        <script
          key="bv-geo-jsonld-ratings-data"
          id="bv-geo-jsonld-ratings-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: ratingsScript }}
        />
      )}
      {reviewSummaryScript && (
        <script
          key="bv-geo-jsonld-reviewSummary-data"
          id="bv-geo-jsonld-reviewSummary-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewSummaryScript }}
        />
      )}
    </Head>
  );
};
